import {useEffect, useState} from 'react'
import { BrowserRouter, useLocation } from 'react-router-dom';
import { Routes, Route, Navigate } from 'react-router-dom';
import "./App.css"
import Landing from "./screens/Landing/Landing"
import AppBar from "./components/AppBar/AppBar"
import Invoices from "./screens/Invoices/Invoices"
import InvoiceDetail from "./screens/InvoiceDetail/InvoiceDetail"
import { useAppDispatch, useAppSelector } from "./app/hooks"
import { openInvoices, selectActivePage, selectLGInternalUser, setLGInternalUser } from "./activeScreenSlice"
import { Alert, Box, Snackbar, CircularProgress } from "@mui/material"
import { styled } from "@mui/system"
import Settings from "./screens/Settings/Settings"
import { useCurrentUser, useLogoutUser, useSsoToken } from "./hooks/Users"
import { UserRole } from "./api/generated"
import getInitials from "./util/getInitials"
import { closeToast } from "./toastSlice"
import Interceptor from "./components/Interceptor/Interceptor"
import Login from "./screens/Login/Login"
import {openLogin} from './activeScreenSlice'
import useSessionTimeout from './hooks/useSessionTimeout'
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './components/AuthContext/AuthContext';
import getCookieByName from './util/getCookieByName';
import useShowError from "./hooks/useShowError"
import Reporting from './screens/Reporting/Reporting';
import { FilterValues } from './components/InvoicesTable/InvoiceFilterPanel'
import dayjs from "dayjs";

export const AppWrapper = styled(Box)({
  display: "grid",
  gridTemplateColumns: "auto",
  gridTemplateRows: "56px 1fr",
  height: "100vh",
})

export const ContentWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  minHeight: "calc(100vh - 58px)",
})
const initialFilterValues: FilterValues = {
  businessUnit: null,
  queueName: null,
  assignedUserEmail: null,
  invoiceStartDate: null,
  invoiceEndDate: null,
  invoiceAge: null,
  currency: null,
  invoiceAmountRange: null,
  supplierName: null,
  supplierSite: null,
};

function MainApp() {
   const location = useLocation();
  // const [LGInternalUser, setLGInternalUser] = useState(false);
const LGInternalUser = useAppSelector(selectLGInternalUser);
const dispatch = useAppDispatch()

  useEffect(() => {
    // If path starts with /internal, set to true, else false
    if(location.pathname.startsWith('/internal')){
      dispatch(setLGInternalUser(true))
    }else{
      dispatch(setLGInternalUser(false))
    }
  }, [location.pathname]);

  const activePage = useAppSelector(selectActivePage)
  const { authToken, setAuthToken, sessionTime, setSessionTime } = useAuth();

  const toast = useAppSelector((state) => state.toast)
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const currentUser = useCurrentUser();
  const {isLoading} = useCurrentUser();
  const logoutMutation = useLogoutUser();
  const getSsoTokenMutation = useSsoToken();
  const queryClient = useQueryClient();
  const showError = useShowError();
  const [filters, setFilters] = useState<FilterValues>(initialFilterValues);
  const [filteredData, setFilteredData] = useState({});
  const [sortModel, setSortModel] = useState([]);
  const [invFilters,setInvFilters]=useState({})
  const [paginationModel, setPaginationModel] = useState({page: 0,pageSize: 10});
  const startOfMonth = dayjs().startOf('month');
  const currentDate = dayjs();
  const [globalRptFilters, setGlobalRptFilters] = useState({
    business_unit: [],
    supplier: [],
    invoice_received_start: startOfMonth,
    invoice_received_end: currentDate,
    invoice_generation_start: null,
    invoice_generation_end: null,
    invoiceReceiveDate: [startOfMonth, currentDate],
    invoiceGenerationDate: [null, null],
  });
  const [tabLocalFilters, setTabLocalFilters] = useState({
  0: {}, // Invoice Operations
  1: {}, // Invoice Complexity
  2: {}, // Suppliers
  3: {}, // Business Unit
  4: {}, // Team & Reviewer Performance
});
  useEffect(() => {
    const isLoggedIn = getCookieByName('ssoEnabledLogin');

    if (isLoggedIn && localStorage.getItem('ssoEnabled') === null) {
      getSsoTokenMutation.mutate(undefined,{
        onSuccess: (response) => {
          setAuthToken(response.access_token);
          setSessionTime(response.session_inactive_timeout_minutes);
          localStorage.setItem("expiration", response.token_expiry);
          localStorage.setItem("ssoEnabled", "true");
          dispatch(openInvoices())
        },
        onError: (error) => {
          showError(error?.message)
        }
      });
    }
  }, []);

  const clearSessionTokens = () => {
    localStorage.removeItem("expiration");
    localStorage.removeItem("ssoEnabled");
    setAuthToken("");
    setIsLoggedIn(false);
    dispatch(openLogin());
    queryClient.cancelQueries();
    queryClient.removeQueries();
  }

  const onLogoutClick = () => {
    logoutMutation.mutate(undefined,{
      onSuccess: () => {
        clearSessionTokens()
      },
      onError: () => {
        localStorage.removeItem("expiration");
        localStorage.removeItem("ssoEnabled");
        setAuthToken("");
        setIsLoggedIn(false);
        dispatch(openLogin());
      }
    });
  }

  useSessionTimeout(onLogoutClick, isLoggedIn, sessionTime);

  function getAppbarVariant() {
    if (activePage.activeScreen === "landing") {
      return "landing"
    } else if (activePage.activeScreen === "login") {
      return "login"
    }

    return currentUser?.data?.user_role!
  }

  function parseInitials() {
    if (activePage.activeScreen === "landing" || !currentUser?.data) {
      return undefined
    }

    return getInitials(currentUser?.data?.name)
  }

  function handleCloseToast() {
    dispatch(closeToast())
  }

  useEffect(()=>{
    if(localStorage.getItem("expiration") === null && authToken === "") {
      setIsLoggedIn(false);
    } else if(authToken?.length > 0){
      setIsLoggedIn(true);
      const isShowSettings =
      activePage.activeScreen !== "landing" &&
      (currentUser?.data?.role?.name.toLowerCase() === UserRole.CLIENT_ADMIN ||
        currentUser?.data?.role?.name.toLowerCase() === UserRole.SYSTEM_ADMIN || 
        currentUser?.data?.role?.name.toLowerCase() === UserRole.TEAM_LEAD
      )
      setShowSettings(isShowSettings);
    }
  },[authToken, currentUser])

  return (
      <AppWrapper>
        <Interceptor>
          <Snackbar
            open={toast.open}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            autoHideDuration={toast.options.autoHideDuration}
            onClose={handleCloseToast}
          >
            <Alert
              onClose={handleCloseToast}
              severity={toast.options.severity}
              variant="filled"
              sx={{ width: "100%" }}
            >
              {toast.message}
            </Alert>
          </Snackbar>
          <Box className={getAppbarVariant() === "login" || getAppbarVariant() === "landing"? "bodyBg" : ""}>
          <AppBar
            variant={getAppbarVariant()!}
            name={currentUser?.data?.name}
            showSettings={showSettings}
            role={currentUser?.data?.role?.name}
            isTokenValid={!(localStorage.getItem("expiration") === null)}
            onLogoutClick={onLogoutClick}
          />
          {
            <ContentWrapper>
              {isLoggedIn && currentUser?.data && currentUser?.data.user_role && activePage.activeScreen === "invoiceDetail" && (
                <InvoiceDetail
                  currentUserId={currentUser?.data?.id}
                  currentUserRole={currentUser?.data?.user_role}
                />
              )}
              {activePage.activeScreen === "login" && <Login />}
              {activePage.activeScreen === "landing" && <Landing />}
              {isLoggedIn && activePage.activeScreen === "invoices" && (
                isLoading ? <CircularProgress sx={{margin: '0 auto', mt: "5%"}}/> : <Invoices userRole={currentUser?.data?.role?.name} filters={filters} setFilters = {setFilters} filteredData={filteredData} setFilteredData={setFilteredData} invFilters={invFilters} setInvFilters = {setInvFilters} sortModel={sortModel} setSortModel={setSortModel} paginationModel={paginationModel} setPaginationModel={setPaginationModel} />
              )}
              {activePage.activeScreen === "reporting" && <Reporting 
                globalRptFilters={globalRptFilters} 
                setGlobalRptFilters={setGlobalRptFilters} 
                tabLocalFilters={tabLocalFilters}
                setTabLocalFilters = {setTabLocalFilters}
              />}
              {activePage.activeScreen === "settings" && <Settings />}
            </ContentWrapper>
          }
          </Box>
        </Interceptor>
      </AppWrapper>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Main app route (handles / and all other paths) */}
        <Route path="/*" element={<MainApp />} />
      </Routes>
    </BrowserRouter>
  );
}
